import React, { useEffect, useState, useContext } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { FluentProvider, webLightTheme, Divider } from '@fluentui/react-components';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { withComponentErrorLogging } from '@bingads-webui-campaign-react/global-error-options';
import { withLoggingContext, useCopilotLoggers } from '@bingads-webui-campaign-react/copilot-loggers';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { getLogVersion } from '@bingads-webui-campaign-react/asset-library-model';
import {
  getLoggerWithoutPageInfo,
  createVideoRepository,
  createImageRepository,
  createFolderRepository,
  uploadVideoMetadata,
} from '@bingads-webui-campaign-react/asset-library-services';
import { newVideoFromMTObject } from '@bingads-webui-campaign-react/image-experience-video-part';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';


import { saveAds, getDealDetails } from '../model';
import { VideoAdsContainer } from './video-ads-container';
import { AdPreview } from './ad-preview';
import { AdEditorFooter } from './ad-editor-footer';

const getAdsStepStyles = (/* components, theme */) => ({
  videoPanelRoot: {
    marginTop: 20,
    width: 500,
    minHeight: 181,
    padding: 20,
    paddingBottom: 20,
    background: '#FFFFFF',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    borderRadius: 6,
    '& .image-list-item-status-indicator': {
      background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%)',
      borderRadius: 6,
      width: '170px !important',
      height: '96px !important',
      paddingTop: '10px !important',
      '&:hover': {
        '& .edit-button': {
          display: 'none',
        },
      },
      '& .placeholder-content': {
        color: 'white !important',
      },
    },
    '& .error': {
      marginTop: 20,
    },
    '& .image-experience-content': {
      width: 170,
      '& .col-sm-3': {
        background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%)',
        borderRadius: 6,
        width: '170px !important',
        height: '96px !important',
        paddingTop: '10px !important',
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'column',
        alignItems: 'center',
        '&:hover': {
          '& .edit-button': {
            display: 'none',
          },
        },
        '& .placeholder-content': {
          color: 'white !important',
        },
        '& .ms-Button': {
          width: 56,
          height: 24,
          marginTop: 10,
        },
        '& .image-list-content': {
          width: '170px !important', // fixed for 16:9 aspect ratio
          border: 'none !important',
          padding: '0px !important',
        },
      },
    },
    '& .image-section-label': {
      display: 'flex',
      alignItems: 'center',
    },
    '& .image-list-label': {
      fontWeight: 600,
    },
    '& .image-list-content': {
      width: '170px !important', // fixed for 16:9 aspect ratio
      border: 'none !important',
      padding: '0px !important',
    },
    '& .image-list-item-video': {
      position: 'relative',
      width: '170px !important', // fixed for 16:9 aspect ratio
      '&:hover': {
        '& .edit-button': {
          display: 'flex',
        },
      },
      '& .placeholder-content': {
        color: 'black !important',
      },

    },
    '& .image-item-ratio-label': {
      paddingTop: 10,
    },
    '& .thumbnail-preview': {
      borderRadius: 6,
      filter: 'drop-shadow(0px 0.6px 1.8px rgba(0, 0, 0, 0.1)) drop-shadow(0px 3.2px 7.2px rgba(0, 0, 0, 0.13))',
      width: '170px !important',
      height: '96px !important',
    },
  },
  selectedPanel: {
    border: '1px solid #0078D4',
  },
  videoPanelHeader: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
  },
  adPreviewTitle: {
    fontSize: 18,
    fontWeight: 600,
  },
  previewContainer: {
    width: 498,
  },
  adPreviewHeader: {
    height: 40,
  },
  adPreviewRoot: {
    width: 540,
    padding: 20,
    paddingBottom: 20,
    display: 'flex',
    flexWrap: 'wrap',
  },
  video: {
    borderRadius: '6px',
    filter: 'drop-shadow(0px 0.6px 1.8px rgba(0, 0, 0, 0.1)) drop-shadow(0px 3.2px 7.2px rgba(0, 0, 0, 0.13))',
  },
  previewTag: {
    marginTop: 10,
  },
  pagination: {
    paddingTop: 10,
    width: '100%',
  },
  pageControls: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
  },
  paginationIndicatorDot: {
    height: 8,
    width: 8,
    borderRadius: '50%',
    display: 'inline-block',
    cursor: 'pointer',
  },
  paginationButton: {
    height: 8,
    width: 8,
    opacity: 0,
  },
  paginationIndicator: {
    display: 'flex',
    columnGap: '2px',
  },
  videoPanelContent: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  placementInfoTile: {
    paddingTop: 20,
    marginLeft: 10,
  },
});

const containerStyle = {
  display: 'flex',
  '& .image-list-content': {
    border: 'none !important',
    padding: '0px !important',
  },
};

const AdEditor = ({
  aid,
  cid,
  i18n,
  perfMarker,
  permissions,
  adsModel,
  campaignId,
  adGroupId,
  onAdsValidation,
  oDataConfig,
  resetFields,
  biddingScheme,
  isXandrServingCampaign,
  onCancel,
  onSave,
  onSaveAndCreateAnother,
  isMultiAdFormatAd,
  loadClipchampPlayer,
}) => {
  const [deal, setDeal] = useState(null);
  const [dealLoaded, setDealLoaded] = useState(false);
  const [serverErrors, setServerErrors] = useState(null);
  const [blocking, setBlocking] = useState(false);
  const instrumentation = useContext(InstrumentationContext);
  const { logSuggestionsApplied } = useCopilotLoggers();

  useEffect(() => {
    const fetchDeal = async () => {
      const dealDetail = await getDealDetails(cid, aid, campaignId);
      setDeal(dealDetail);
      setDealLoaded(true);
    };

    fetchDeal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scenario = instrumentation.createScenario('XandrVideoAdsAdEditing');
  const activity = scenario.createActivity('Ad');
  const versionForLog = getLogVersion(permissions);
  const logger = getLoggerWithoutPageInfo(activity, versionForLog, 'XandrVideoAdsAdEditing');

  const videoRepository = createVideoRepository({
    oDataConfig,
    aid,
    cid,
  });
  const folderRepository = createFolderRepository({
    i18n,
    logger,
    oDataConfig,
    aid,
    cid,
  });
  const imageRepository = createImageRepository({
    oDataConfig,
    aid,
    cid,
    logger,
    i18n,
    activity,
  });

  const saveAd = () => saveAds({
    ads: isMultiAdFormatAd ? { ...adsModel, adSubType: 'Video' } : adsModel,
    campaign: { Id: campaignId },
    adGroup: { Id: adGroupId },
    activity,
  }, { scenario, i18n }).then((adData) => {
    const suggestionIds = _.chain(adsModel.data)
      .map(ad => ad.data.video.recommendationId).compact().value();

    logSuggestionsApplied(
      campaignId,
      adGroupId,
      undefined /* textSuggestions */,
      undefined /* imageSuggestionIds */,
      suggestionIds
    );
    return adData;
  }).finally(() => setBlocking(false));

  const saveVideoAd = () => {
    setBlocking(true);

    if (adsModel.data[0].data.video.id) {
      return saveAd();
    }

    const videoMetaData = _.omit(adsModel.data[0].data.video, 'savedMaterial');
    const { savedImages, savedLogo } = adsModel.data[0].data.video.savedMaterial || {};

    return uploadVideoMetadata({
      videoMetadata: videoMetaData,
      newVideoFromMTObject,
      savedImages,
      savedLogo,
      folderRepository,
      imageRepository,
      videoRepository,
      logger,
    }).then((uploadedVideo) => {
      // eslint-disable-next-line no-param-reassign
      adsModel.data[0].data.video = uploadedVideo;
      return saveAd();
    });
  };

  const save = (saveAnother) => {
    setServerErrors(null);

    if (!adsModel.isValid) {
      return Promise.resolve(false);
    }

    return saveVideoAd().then((result) => {
      if (result && result.ads && result.ads.data) {
        if (saveAnother) {
          onSaveAndCreateAnother(result);
        } else {
          onSave(result);
        }
      }
    }).catch((result) => {
      setServerErrors(result.serverErrors);
    });
  };

  const cancel = () => {
    onCancel();
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <UiBlocker
        blocking={blocking}
        loadingText={i18n.getString(_TL_('Loading...'))}
        classes={{
          blockUiContainer: 'blocker-global',
          blockerIndicator: 'blocker-indicator',
        }}
      >
        <div>
          <div style={containerStyle}>
            <div>
              <VideoAdsContainer
                aid={aid}
                cid={cid}
                campaignId={campaignId}
                i18n={i18n}
                permissions={permissions}
                perfMarker={perfMarker}
                ads={adsModel.data}
                onAdsValidation={onAdsValidation}
                oDataConfig={oDataConfig}
                disablePagination
                deal={deal}
                dealLoaded={dealLoaded}
                customStyleFunction={getAdsStepStyles}
                useCustomStyleFunction
                resetFields={resetFields}
                biddingScheme={biddingScheme}
                isXandrServingCampaign={isXandrServingCampaign}
                allowCopilotVideoGeneration
                isAdGridEditorMode
                loadClipchampPlayer={loadClipchampPlayer}
              />
              <ErrorMessages messages={serverErrors ? serverErrors.map(error => error.message) : []} />
            </div>
            <AdPreview
              i18n={i18n}
              onUpdateCurrentVideoIndex={() => _.noop}
              ads={adsModel}
              disablePagination
              customStyleFunction={getAdsStepStyles}
              useCustomStyleFunction
              permissions={permissions}
              loadClipchampPlayer={loadClipchampPlayer}
            />
          </div>
          <Divider />
          <AdEditorFooter
            cancel={cancel}
            save={() => save(false)}
            saveAndCreateAnother={() => save(true)}
          />
        </div>
      </UiBlocker>
    </FluentProvider>);
};
AdEditor.propTypes = {
  containerStyle: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  aid: PropTypes.string.isRequired,
  cid: PropTypes.string.isRequired,
  campaignId: PropTypes.string.isRequired,
  adGroupId: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  adsModel: PropTypes.shape({
    data: PropTypes.shape({}),
    isValid: PropTypes.bool.isRequired,
  }).isRequired,
  onAdsValidation: PropTypes.func.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  resetFields: PropTypes.bool.isRequired,
  serverErrors: PropTypes.shape({}),
  biddingScheme: PropTypes.shape({}),
  isXandrServingCampaign: PropTypes.bool,
  isMultiAdFormatAd: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSaveAndCreateAnother: PropTypes.func.isRequired,
};

AdEditor.defaultProps = {
  serverErrors: null,
  biddingScheme: {},
  isXandrServingCampaign: false,
  isMultiAdFormatAd: false,
};


const AdEditorWithErrorLogging = withLoggingContext(withComponentErrorLogging(
  AdEditor,
  'XandrVideoAdsAdEditing'
));

export { AdEditorWithErrorLogging as AdEditor };

